import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { setDocuments } from '../store/documentsSlice';
import { resetQuestionnaire, updateAnswers, setStep } from '../store/questionnaireSlice';
import { setProjects } from '../store/projectsSlice';
import { documentApi, questionnaireApi, projectsApi } from '../services/api';

export default function useAppInit() {
  const dispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.auth);
  const selectedTaxYear = useSelector((state: RootState) => state.analysis.selectedTaxYear);

  useEffect(() => {
    if (!token) return;

    // Load documents from backend
    interface RawDocument {
      id: number | string;
      original_name: string;
      document_type: string;
      file_size: number;
      upload_date: string;
    }
    documentApi.getDocuments()
      .then((res) => {
        const docs = ((res.data.documents || []) as RawDocument[]).map((doc) => ({
          id: String(doc.id),
          fileName: doc.original_name,
          documentType: doc.document_type,
          fileSize: doc.file_size,
          uploadDate: doc.upload_date,
          status: 'uploaded' as const,
        }));
        dispatch(setDocuments(docs));
      })
      .catch(() => {
        // Silently fail — user may not have documents yet
      });

    projectsApi.list()
      .then((res) => dispatch(setProjects(res.data)))
      .catch(() => {
        // Projects are created lazily when documents are uploaded.
      });
  }, [token, dispatch]);

  useEffect(() => {
    if (!token) return;

    // Load questionnaire for the currently selected tax year.
    questionnaireApi.getAnswers(selectedTaxYear)
      .then((res) => {
        dispatch(resetQuestionnaire());

        if (res.data.questionnaire_data) {
          let answers = res.data.questionnaire_data;
          if (typeof answers === 'string') {
            try {
              answers = JSON.parse(answers);
            } catch {
              // Ignore malformed JSON — fall through with unparsed payload
            }
          }
          dispatch(updateAnswers(answers));
          // Restore step if saved
          if (answers._currentStep) {
            dispatch(setStep(answers._currentStep));
          }
        }
      })
      .catch(() => {
        // 404 is expected if no questionnaire is saved for this tax year yet.
        dispatch(resetQuestionnaire());
      });
  }, [token, dispatch, selectedTaxYear]);
}
